import { FC, useState, useEffect } from "react";
import {
  IAddSubscription,
  IEditSubscription,
  ISubscription,
} from "../../../../api/Subscriptions/subscriptionType";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast, { Toaster } from "react-hot-toast";
import { Field, Form } from "react-final-form";
import * as yup from "yup";
import {
  Button,
  Grid,
  TextField,
  MenuItem,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import EditSubscriptionService from "../../../../api/Subscriptions/EditSubscription/EditSubscriptionService";
import AddSubscriptionService from "../../../../api/Subscriptions/AddSubscription/AddSubscriptionService";
import { SafeKaroUser } from "../../../../context/constant";
import { setIn } from "final-form";
import { header } from "../../../../context/constant";

interface AddSubsFromProps {
  p?: ISubscription;
}

const toCamelCase = (str: string) => {
  if (str.toLowerCase() === "relationship manager") {
    return "rm";
  }
  return str
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) =>
      index === 0 ? match.toLowerCase() : match.toUpperCase()
    )
    .replace(/\s+/g, "");
};

const AddSubsFrom: FC<AddSubsFromProps> = ({ p }) => {
  const [editorValue, setEditorValue] = useState(p?.planDetails || "");
  const [availableUserOptions, setAvailableUserOptions] = useState([
    "Partner",
    "Booking",
    "Operation",
    "Account",
    "Relationship Manager",
    "Sales",
    "HR",
  ]);
  const [availableDiscountOptions, setAvailableDiscountOptions] = useState(
    ["3", "6", "12", "24"]
  );
  const [selectedUserOption, setSelectedUserOption] = useState("");
  const [userLimitValue, setUserLimitValue] = useState<number | string>("");
  const [userLimits, setUserLimits] = useState<{
    [key: string]: number | string;
  }>({});
  const [selectedDiscountOption, setSelectedDiscountOption] = useState("");
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [discountValue, setDiscountValue] = useState<number | string>("");
  const [discounts, setDiscounts] = useState<{ [key: string]: number }>({});
  const navigate = useNavigate();
  const storedTheme: any = localStorage.getItem("user") as SafeKaroUser | null;
  const UserData = storedTheme ? JSON.parse(storedTheme) : storedTheme;

  useEffect(() => {
    if (p) {
      setUserLimits(p.userLimit || {});
      setDiscounts(p.discount || {});
    }
  }, [p]);
  function isNumber(value: number | string) {
    return typeof value === "number" && !isNaN(value);
  }
  const handleSaveUserLimit = () => {
    if (selectedUserOption) {
      const camelCaseOption = toCamelCase(selectedUserOption);
      let limitValue = isUnlimited ? "Infinity" : Number(userLimitValue);
      if (isNumber(limitValue)) {
        const limitCheck = limitValue as number;
        if (!isUnlimited && (isNaN(limitCheck) || limitCheck <= 0)) {
          alert("Please enter a valid user limit.");
          return;
        }
      }

      setUserLimits((prev) => ({ ...prev, [camelCaseOption]: limitValue }));
      setAvailableUserOptions((prev) =>
        prev.filter((option) => option !== selectedUserOption)
      );
      setSelectedUserOption("");
      setUserLimitValue("");
      setIsUnlimited(false); // Reset the checkbox after saving

      console.log("User limit saved:", { [camelCaseOption]: limitValue });
    } else {
      alert("Please select a user limit option.");
    }
  };

  const handleSaveDiscount = () => {
    if (selectedDiscountOption && discountValue) {
      setDiscounts((prev) => ({
        ...prev,
        [selectedDiscountOption]: Number(discountValue),
      }));
      setAvailableDiscountOptions((prev) =>
        prev.filter((option) => option !== selectedDiscountOption)
      );
      setSelectedDiscountOption("");
      setDiscountValue("");
    }
  };

  const handleDeleteUserLimit = (key: string) => {
    setUserLimits((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    setAvailableUserOptions((prev) => [...prev, key]);
  };

  const handleDeleteDiscount = (key: string) => {
    setDiscounts((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    setAvailableDiscountOptions((prev) => [...prev, key]);
  };

  const callEditSubs = async (payload: IEditSubscription) => {
    try {
      const res = await EditSubscriptionService({
        header,
        editSubs: payload,
        id: p?._id!,
      });
      if (res.status) {
        navigate(-1);
      }
    } catch (error: any) {
      const err = await error;
      toast.error(err.message);
    }
  };

  const callAddSubs = async (payload: IAddSubscription) => {
    try {
      const res = await AddSubscriptionService(payload);
      if (res.status) {
        navigate(-1);
      }
    } catch (error: any) {
      const err = await error;
      toast.error(err.message);
    }
  };

  const validateFormValues = (schema: any) => async (values: any) => {
    if (typeof schema === "function") {
      schema = schema();
    }
    try {
      await schema.validate(values, { abortEarly: false });
    } catch (err: any) {
      const errors = err.inner.reduce((formError: any, innerError: any) => {
        return setIn(formError, innerError.path, innerError.message);
      }, {});
      return errors;
    }
  };

  const validationSchema = yup.object().shape({
    planName: yup
      .string()
      .required("Plan Name is required")
      .min(1, "Plan Name must be at least 1 character")
      .max(100, "Plan Name cannot exceed 100 characters"),
    policyCount: yup.number().required("Policy Count is required"),
  });

  const validate = validateFormValues(validationSchema);

  const onSubmit = (values: any) => {
    values["planDetails"] = editorValue;

    values["userLimit"] = userLimits;

    values["discount"] = discounts;

    if (p) {
      values["updatedBy"] = UserData.name;
      values["header"] = header;
      callEditSubs(values);
    } else {
      values["createdBy"] = UserData.name;
      callAddSubs(values);
    }
  };

  const generateInitialValue = () => {
    return {
      planName: p?.planName,
      monthlyAmount: p?.monthlyAmount,
      policyCount: p?.policyCount,
      userLimit: p?.userLimit,
      discount: p?.discount,
    };
  };

  return (
    <>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <h1 className="text-base font-medium mb-1">User Limit and Discount</h1>
        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-md shadow-sm">
          {Object.entries(userLimits)?.map(([key, value]) => (
            <Chip
              key={key}
              label={`${key}: ${value === "Infinity" ? "Unlimited" : value}`}
              onDelete={() => handleDeleteUserLimit(key)}
            />
          ))}
          {Object.entries(discounts).map(([key, value]) => (
            <Chip
              key={key}
              label={`${key} Month - ${value}%`}
              onDelete={() => handleDeleteDiscount(key)}
            />
          ))}
        </div>
      </Grid>
      <Form
        onSubmit={onSubmit}
        initialValues={generateInitialValue()}
        validate={validate}
        render={({ handleSubmit, submitting, errors }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Field name="planName">
                  {({ input, meta }) => (
                    <TextField
                      {...input}
                      onBlur={() => input.onChange(input.value.trim())}
                      onChange={(event) => input.onChange(event.target.value.trimStart())}
                      label="Enter Plan Name"
                      variant="outlined"
                      className="rounded-sm w-full"
                      size="small"
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Field name="policyCount">
                  {({ input, meta }) => (
                    <TextField
                      {...input}
                      label="Enter Policy Limit"
                      variant="outlined"
                      className="rounded-sm w-full"
                      type="number"
                      size="small"
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <Field name="monthlyAmount">
                  {({ input, meta }) => (
                    <TextField
                      {...input}
                      label="Enter Monthly Amount"
                      variant="outlined"
                      className="rounded-sm w-full"
                      type="number"
                      size="small"
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>
              </Grid>
              <Grid item lg={5} md={5} sm={6} xs={12}>
                <TextField
                  select
                  label="Select User Limit"
                  variant="outlined"
                  className="rounded-sm w-full"
                  size="small"
                  value={selectedUserOption}
                  onChange={(e) => setSelectedUserOption(e.target.value)}
                >
                  <MenuItem value="">
                    <em></em>
                  </MenuItem>
                  {availableUserOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item lg={4} md={5} sm={6} xs={12} >
                <TextField
                  label="Enter User Limit Value"
                  variant="outlined"
                  className="rounded-sm w-full"
                  type="number"
                  size="small"
                  value={isUnlimited ? "" : userLimitValue}
                  onChange={(e) => setUserLimitValue(e.target.value)}
                  disabled={isUnlimited}
                />
              </Grid>

              <Grid item lg={1} md={2} sm={6} xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isUnlimited}
                      onChange={(e) => setIsUnlimited(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Unlimited"
                />
              </Grid>

              <Grid item lg={1} md={2} sm={6} xs={12}>
                <Button
                  className="h-8 w-full bg-addButton ml-2  text-white p-3 text-[11px] rounded-sm"
                  variant="contained"
                  color="primary"
                  onClick={handleSaveUserLimit}>
                  Save
                </Button>
              </Grid>

              <Grid item lg={5} md={5} sm={6} xs={12}>
                <TextField
                  select
                  label="Select Discount Month"
                  variant="outlined"
                  className="rounded-sm w-full"
                  size="small"
                  value={selectedDiscountOption}
                  onChange={(e) => setSelectedDiscountOption(e.target.value)}
                >
                  <MenuItem value="">
                    <em></em>
                  </MenuItem>
                  {availableDiscountOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item lg={5} md={5} sm={6} xs={12}>
                <TextField
                  label="Enter Discount Percentage"
                  variant="outlined"
                  className="rounded-sm w-full"
                  type="number"
                  size="small"
                  value={discountValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numericValue = Number(value);
                    if (numericValue <= 100) {
                      setDiscountValue(value);
                    } else {
                      toast.error("Discount value cannot exceed 100%");
                    }
                  }}
                />
              </Grid>
              <Grid item lg={1} md={2} sm={6} xs={12}>
                <Button
                  className="h-8 w-full ml-2 bg-addButton  text-white p-3  rounded-sm text-[11px]"
                  variant="contained"
                  color="primary"
                  onClick={handleSaveDiscount}
                >
                  Save
                </Button>
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <label className="font-semibold">Description</label>
                <ReactQuill
                  theme="snow"
                  value={editorValue}
                  onChange={setEditorValue}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  disabled={submitting}
                  variant="contained"
                  color="primary"
                  className="w-26 h-10 bg-addButton text-white p-3 text-xs rounded-sm"
                >
                  {p ? "Update Subscription" : "Add Subscription"}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      />
      <Toaster position="bottom-center" reverseOrder={false} />
    </>
  );
};

export default AddSubsFrom;
